import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import config from '../../config';
import { ensureCurrentUser } from '../../util/data';
import {
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    Footer,
} from '../../components';
import { TopbarContainer } from '..';
import css from './FindListingsPage.css';
import {
    SectionHero,
    SectionFreeAdvertise,
    SectionHowItWorks,
    SectionAdvantages,
    SectionCommunityFeedbacks,
    SectionFAQ,
    SectionInterestingNearby,
} from '../LandingPage/sections';
import { useIsMobile } from '../../hooks/useIsMobile';
import constructHIWOptionsByType from './constructHIWOptionsByType';
import { jsonLd as jsonLdHorse } from './json-ld-horse';
import { jsonLd as jsonLdListingRider } from './json-ld-listing-rider';

const { siteTitle, userTypeRider, userTypeHorseowner, listingTypeHorse, listingTypeRider } = config;

const listingToUserTypeMapper = {
    [listingTypeHorse]: userTypeRider,
    [listingTypeRider]: userTypeHorseowner,
};

export const FindListingsPage = ({ history, type, intl, scrollingDisabled, currentUser }) => {
    const [isMobile, , computing] = useIsMobile(1024);

    const schemaTitle = intl.formatMessage(
        { id: `FindListingsPage.schemaTitle-find-${type}` },
        { siteTitle }
    );
    const schemaDescription = intl.formatMessage({
        id: `FindListingsPage.schemaDescription-find-${type}`,
    });

    const isFindHorsePage = type === listingTypeHorse;

    const jsonLd = isFindHorsePage ? jsonLdHorse : jsonLdListingRider;

    return (
        <Page
            scrollingDisabled={scrollingDisabled}
            contentType="website"
            description={schemaDescription}
            title={schemaTitle}
            facebookImages={[
                {
                    url: 'https://horsedeal.imgix.net/static/social-image-1200x630.webp',
                    width: 1200,
                    height: 630,
                },
            ]}
            twitterImages={[
                {
                    url: 'https://horsedeal.imgix.net/static/social-image-600x314.webp',
                    width: 600,
                    height: 314,
                },
            ]}
            schema={{
                '@context': jsonLd['@context'],
                '@type': jsonLd['@type'],
                mainEntity: jsonLd['mainEntity'],
                description: schemaDescription,
                name: schemaTitle,
                image: ['https://horsedeal.imgix.net/static/social-image-1200x630.webp'],
            }}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <TopbarContainer />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    className={css.layout}
                    blobBackground
                    blobProps={{
                        xDistance: 20,
                        baseWidth: 760,
                    }}
                >
                    <main className={css.container}>
                        <SectionHero
                            userType={listingToUserTypeMapper[type]}
                            computing={computing}
                            isMobile={isMobile}
                            currentUser={currentUser}
                            type={type}
                        />
                        <SectionFreeAdvertise type={type} history={history} />
                        {computing ? null : (
                            <SectionHowItWorks
                                options={constructHIWOptionsByType(type, intl)}
                                type={type}
                                isMobile={isMobile}
                            />
                        )}
                        <SectionAdvantages type={type} history={history} />
                        <SectionCommunityFeedbacks
                            headingId={`SectionCommunityFeedbacks.heading-for-${type}`}
                            descriptionId={`SectionCommunityFeedbacks.description-for-${type}`}
                            type={type}
                            history={history}
                        />
                        <SectionFAQ
                            descriptionId={`SectionFAQ.description-for-${type}`}
                            questionsSet={`find-${type}`}
                        />
                        <SectionInterestingNearby
                            type={type}
                            subHeadingId={`SectionInterestingNearby.subHeading-for-${type}`}
                            headingId={`SectionInterestingNearby.heading-for-${type}`}
                            descriptionId={`SectionInterestingNearby.description-for-${type}`}
                        />
                    </main>
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

const { bool, object, array } = PropTypes;

FindListingsPage.propTypes = {
    scrollingDisabled: bool.isRequired,

    // from withRouter
    history: object.isRequired,
    location: object.isRequired,

    // from injectIntl
    intl: intlShape.isRequired,
};

const mapStateToProps = ({ user: { currentUser }, UI }) => ({
    currentUser: ensureCurrentUser(currentUser),
    scrollingDisabled: isScrollingDisabled({ UI }),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671

export default compose(withRouter, connect(mapStateToProps), injectIntl)(FindListingsPage);
